import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { Plus, Pencil, Trash2, Loader2, Wallet, LineChart, PiggyBank, AlertTriangle, Building2 } from "lucide-react";
import { toast } from "sonner";
import RealEstate from "@/components/RealEstate";
import { InfoHint } from "@/components/InfoHint";

const TYPES = { cash: "Cash / Compte", bourse: "Investi (Bourse)", autre: "Autre" };
const NW_COLORS = { Immobilier: "#B08968", Bourse: "#2F6F5E", Cash: "#22293A" };
const emptyAcc = { name: "", type: "cash", balance: "", institution: "", color: "#22293A" };
const SAV_COLORS = { balance: "#22293A", cash: "#C2492A", bourse: "#2F6F5E" };

function SeriesToggle({ items, hidden, onToggle, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={testid}>
      {items.map((it) => {
        const off = hidden.includes(it.key);
        return (
          <button
            key={it.key}
            type="button"
            onClick={() => onToggle(it.key)}
            data-testid={`${testid}-${it.key}`}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${off ? "border-border bg-transparent text-muted-foreground/50" : "border-border bg-secondary text-foreground"}`}
          >
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: off ? "transparent" : it.color, border: `1.5px solid ${it.color}` }} />
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Savings() {
  const [accounts, setAccounts] = useState([]);
  const [proj, setProj] = useState(null);
  const [nw, setNw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyAcc);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nwHidden, setNwHidden] = useState([]);
  const [savHidden, setSavHidden] = useState([]);
  const toggleIn = (setter) => (k) => setter((h) => (h.includes(k) ? h.filter((x) => x !== k) : [...h, k]));
  const [bourseRate, setBourseRate] = useState(0);
  const [savingsSettings, setSavingsSettings] = useState({});

  const defaultMortgageDate = (() => {
    const d = new Date(); d.setFullYear(d.getFullYear() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();
  const [mortgageDate, setMortgageDate] = useState(defaultMortgageDate);
  const [mortgageLoans, setMortgageLoans] = useState([]);
  const [mortgageSelected, setMortgageSelected] = useState(null); // null = tous
  const [mortgageLoading, setMortgageLoading] = useState(false);

  const loadMortgage = useCallback((dateStr) => {
    setMortgageLoading(true);
    api.get(`/loans/projected-balance?date=${dateStr}-01`)
      .then((r) => {
        setMortgageLoans(r.data.loans || []);
        setMortgageSelected((prev) => prev === null ? new Set((r.data.loans || []).map((l) => l.id)) : prev);
      })
      .catch(() => toast.error("Impossible de charger les crédits"))
      .finally(() => setMortgageLoading(false));
  }, []);
  useEffect(() => { loadMortgage(mortgageDate); }, [mortgageDate, loadMortgage]);

  const toggleMortgage = (id) => setMortgageSelected((s) => {
    const next = new Set(s || []);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const selectedSet = mortgageSelected || new Set();
  const mortgageTotal = mortgageLoans.filter((l) => selectedSet.has(l.id)).reduce((a, l) => a + (l.remaining_balance || 0), 0);
  const mortgagePaid = mortgageLoans.filter((l) => selectedSet.has(l.id)).reduce((a, l) => a + (l.paid_principal || 0), 0);

  const load = () => {
    setLoading(true);
    api.get("/savings/accounts").then((r) => setAccounts(r.data));
    api.get("/net-worth").then((r) => setNw(r.data));
    api.get("/savings/settings").then((r) => { setSavingsSettings(r.data); setBourseRate(r.data.bourse_annual_return || 0); });
    api.get("/savings/projection?months=12").then((r) => setProj(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const refreshTotals = useCallback(() => {
    api.get("/savings/accounts").then((r) => setAccounts(r.data));
    api.get("/net-worth").then((r) => setNw(r.data));
    api.get("/savings/projection?months=12").then((r) => setProj(r.data));
  }, []);

  const openNew = () => { setForm(emptyAcc); setEditId(null); setOpen(true); };
  const openEdit = (a) => { setForm({ name: a.name, type: a.type, balance: String(a.balance), institution: a.institution || "", color: a.color || "#22293A" }); setEditId(a.id); setOpen(true); };

  const saveAcc = async () => {
    if (!form.name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      const payload = { ...form, balance: Number(form.balance) || 0 };
      if (editId) await api.put(`/savings/accounts/${editId}`, payload);
      else await api.post("/savings/accounts", payload);
      toast.success(editId ? "Compte modifié" : "Compte ajouté");
      setOpen(false); refreshTotals();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const removeAcc = async (id) => { await api.delete(`/savings/accounts/${id}`); toast.success("Compte supprimé"); refreshTotals(); };

  const saveRate = async () => {
    try {
      await api.put("/savings/settings", { monthly_income: savingsSettings.monthly_income || 0, bourse_annual_return: Number(bourseRate) || 0 });
      toast.success("Rendement bourse appliqué");
      refreshTotals();
      api.get("/savings/settings").then((r) => setSavingsSettings(r.data));
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="savings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Épargne</h1>
          <p className="text-muted-foreground text-sm mt-1">Vos comptes d'épargne et leur évolution prévue sur 12 mois.</p>
        </div>
        <Button onClick={openNew} data-testid="add-account-button"><Plus className="w-4 h-4 mr-1.5" />Nouveau compte</Button>
      </div>

      {loading || !proj ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <>
          {nw && (
            <>
              <Card className="p-5 border-border" data-testid="net-worth-section">
                <div className="flex items-center gap-2 mb-1"><Building2 className="w-4 h-4 text-primary" /><h3 className="font-display font-semibold text-base">Patrimoine global net</h3>
                  <InfoHint testid="net-worth-info" text="Immobilier (valeur actuelle de vos biens, part détenue, moins le capital de crédit restant dû) + Bourse (comptes investis) + Cash (comptes d'épargne + comptes en banque). C'est ce que vous possédez réellement, dettes déduites." />
                </div>
                <p className="text-xs text-muted-foreground mb-4">Immobilier (net de crédits), bourse et cash réunis.</p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                  <div className="h-60" data-testid="net-worth-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={[{ name: "Immobilier", value: Math.max(0, nw.immobilier) }, { name: "Bourse", value: Math.max(0, nw.bourse) }, { name: "Cash", value: Math.max(0, nw.cash) }]} dataKey="value" nameKey="name" innerRadius={62} outerRadius={92} paddingAngle={2}>
                          {["Immobilier", "Bourse", "Cash"].map((n) => <Cell key={n} fill={NW_COLORS[n]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 13 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    <div className="pb-3 border-b border-border"><div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Total net</div><div className="font-num text-3xl mt-1" data-testid="net-worth-total">{fmtEuro(nw.total)}</div></div>
                    {[["Immobilier", nw.immobilier], ["Bourse", nw.bourse], ["Cash", nw.cash]].map(([n, v]) => (
                      <div key={n} className="flex items-center gap-3" data-testid={`net-worth-${n}`}>
                        <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: NW_COLORS[n] }} />
                        <span className="text-sm flex-1">{n}</span>
                        <span className="text-xs text-muted-foreground">{nw.total > 0 ? Math.round(v / nw.total * 100) : 0}%</span>
                        <span className="font-num text-sm font-medium w-28 text-right">{fmtEuro(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>

              <Card className="p-5 border-border" data-testid="net-worth-projection">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-display font-semibold text-base">Projection du patrimoine</h3>
                  <InfoHint testid="projection-info" text="Patrimoine net actuel projeté à 3, 6 et 12 mois selon : votre solde courant net (rentrées − dépenses du mois type), les rentrées/dépenses exceptionnelles prévues sur la période (Vue Annuelle), et le capital de crédit remboursé (qui augmente la part détenue de vos biens). La valeur des biens et de la bourse est supposée constante." />
                </div>
                <p className="text-xs text-muted-foreground mb-4">Selon vos dépenses courantes et les rentrées / dépenses exceptionnelles à venir.</p>
                <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="bourse-rate-control">
                  <span className="text-sm text-muted-foreground">Rendement bourse estimé :</span>
                  <input type="number" step="0.5" value={bourseRate} onChange={(e) => setBourseRate(e.target.value)}
                    className="w-20 h-8 rounded-md border border-border bg-background px-2 text-sm font-num" data-testid="bourse-rate-input" />
                  <span className="text-sm text-muted-foreground">% / an</span>
                  <Button size="sm" variant="outline" className="h-8" onClick={saveRate} data-testid="bourse-rate-save">Appliquer</Button>
                  <InfoHint testid="bourse-rate-info" text="Rendement annuel estimé appliqué à vos comptes Bourse dans les projections (capitalisation mensuelle). La valeur des biens et le cash ne sont pas affectés." />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {nw.horizons.map((h) => (
                    <div key={h.months} className="rounded-xl border border-border p-4" data-testid={`horizon-${h.months}`}>
                      <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Dans {h.label}</div>
                      <div className="font-num text-2xl mt-1">{fmtEuro(h.total)}</div>
                      <div className={`text-xs font-num mt-1 ${h.delta < 0 ? "text-destructive" : "text-emerald-600"}`}>{h.delta >= 0 ? "+" : ""}{fmtEuro(h.delta)}</div>
                      <div className="text-[11px] text-muted-foreground mt-2 space-y-0.5">
                        <div>Courant net : {h.recurring_net >= 0 ? "+" : ""}{fmtEuro(h.recurring_net * h.months)}</div>
                        <div>Rentrées except. : +{fmtEuro(h.exc_income)}</div>
                        <div>Dépenses except. : -{fmtEuro(h.exc_expense)}</div>
                        {h.immo_principal > 0 && <div>Capital immo remboursé : +{fmtEuro(h.immo_principal)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>

              <Card className="p-5 border-border" data-testid="mortgage-projection">
                <div className="flex items-center gap-2 mb-1">
                  <Building2 className="w-4 h-4 text-primary" />
                  <h3 className="font-display font-semibold text-base">Capital restant dû à une date future</h3>
                  <InfoHint testid="mortgage-projection-info" text="Choisissez une date et cochez les crédits à inclure : le capital restant dû est lu directement dans le tableau d'amortissement de chaque crédit à la fin du mois sélectionné." />
                </div>
                <p className="text-xs text-muted-foreground mb-4">Sélectionnez une date et les crédits concernés pour connaître ce qu'il vous restera à rembourser.</p>

                <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="mortgage-date-control">
                  <span className="text-sm text-muted-foreground">À la date de :</span>
                  <input type="month" value={mortgageDate} onChange={(e) => setMortgageDate(e.target.value)}
                    className="h-8 rounded-md border border-border bg-background px-2 text-sm font-num" data-testid="mortgage-date-input" />
                </div>

                {mortgageLoading ? (
                  <div className="h-24 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
                ) : mortgageLoans.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Aucun crédit enregistré. Ajoutez un bien et son crédit dans la section Immobilier ci-dessous.</p>
                ) : (
                  <>
                    <div className="space-y-1.5 mb-4" data-testid="mortgage-loan-list">
                      {mortgageLoans.map((l) => (
                        <label key={l.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-secondary/50 transition-colors" data-testid={`mortgage-loan-${l.id}`}>
                          <input type="checkbox" checked={selectedSet.has(l.id)} onChange={() => toggleMortgage(l.id)}
                            className="w-4 h-4 accent-primary shrink-0" data-testid={`mortgage-loan-check-${l.id}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{l.property_name} <span className="text-muted-foreground font-normal">· {l.name}</span></div>
                            <div className="text-[11px] text-muted-foreground">{l.loan_bank || "—"}{!l.has_schedule ? " · pas de tableau d'amortissement" : l.finished ? " · soldé à cette date" : ""}</div>
                          </div>
                          <span className="font-num text-sm font-medium w-28 text-right shrink-0" data-testid={`mortgage-loan-remaining-${l.id}`}>{fmtEuro(l.remaining_balance)}</span>
                        </label>
                      ))}
                    </div>
                    <div className="rounded-xl bg-secondary/60 border border-border p-4 flex flex-wrap items-end justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Capital restant dû ({selectedSet.size} crédit{selectedSet.size > 1 ? "s" : ""})</div>
                        <div className="font-num text-3xl mt-1" data-testid="mortgage-total-remaining">{fmtEuro(mortgageTotal)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[11px] text-muted-foreground">Capital déjà remboursé à cette date</div>
                        <div className="font-num text-lg" data-testid="mortgage-total-paid">{fmtEuro(mortgagePaid)}</div>
                      </div>
                    </div>
                  </>
                )}
              </Card>

              {nw.series && nw.series.length > 0 && (
                <Card className="p-5 border-border" data-testid="net-worth-evolution">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display font-semibold text-base">Évolution du patrimoine par type</h3>
                    <InfoHint testid="evolution-info" text="Projection mois par mois sur 12 mois, détaillée par type : l'immobilier progresse au rythme du capital de crédit remboursé, le cash suit votre solde mensuel (courant + exceptionnel), la bourse reste constante (hors rendement de marché)." />
                  </div>
                  <p className="text-xs text-muted-foreground mb-4">Immobilier, bourse et cash projetés séparément sur 12 mois.</p>
                  <SeriesToggle testid="nw-series-toggle" hidden={nwHidden} onToggle={toggleIn(setNwHidden)} items={[
                    { key: "immobilier", label: "Immobilier", color: NW_COLORS.Immobilier },
                    { key: "bourse", label: "Bourse", color: NW_COLORS.Bourse },
                    { key: "cash", label: "Cash", color: NW_COLORS.Cash },
                  ]} />
                  <div className="h-72" data-testid="net-worth-evolution-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={[{ label: "Auj.", immobilier: nw.immobilier, bourse: nw.bourse, cash: nw.cash }, ...nw.series]}>
                        <defs>
                          <linearGradient id="gImmo" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={NW_COLORS.Immobilier} stopOpacity={0.7} /><stop offset="95%" stopColor={NW_COLORS.Immobilier} stopOpacity={0.5} /></linearGradient>
                          <linearGradient id="gBourse" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={NW_COLORS.Bourse} stopOpacity={0.7} /><stop offset="95%" stopColor={NW_COLORS.Bourse} stopOpacity={0.5} /></linearGradient>
                          <linearGradient id="gCash" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={NW_COLORS.Cash} stopOpacity={0.7} /><stop offset="95%" stopColor={NW_COLORS.Cash} stopOpacity={0.5} /></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={64} />
                        <Tooltip formatter={(v, n) => [fmtEuro(v), n]} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 13 }} />
                        <Area type="monotone" dataKey="immobilier" name="Immobilier" stackId="1" stroke={NW_COLORS.Immobilier} strokeWidth={2} fill="url(#gImmo)" hide={nwHidden.includes("immobilier")} />
                        <Area type="monotone" dataKey="bourse" name="Bourse" stackId="1" stroke={NW_COLORS.Bourse} strokeWidth={2} fill="url(#gBourse)" hide={nwHidden.includes("bourse")} />
                        <Area type="monotone" dataKey="cash" name="Cash" stackId="1" stroke={NW_COLORS.Cash} strokeWidth={2} fill="url(#gCash)" hide={nwHidden.includes("cash")} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </Card>
              )}
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="p-5" data-testid="savings-total"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><PiggyBank className="w-4 h-4" />Épargne totale</div><div className="font-num text-2xl mt-2">{fmtEuro(proj.current_total)}</div></Card>
            <Card className="p-5"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><Wallet className="w-4 h-4" />Cash</div><div className="font-num text-2xl mt-2">{fmtEuro(proj.cash_total)}</div></Card>
            <Card className="p-5"><div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground font-semibold"><LineChart className="w-4 h-4" />Investi (Bourse)</div><div className="font-num text-2xl mt-2">{fmtEuro(proj.invest_total)}</div></Card>
          </div>

          <Card className="p-5 border-border">
            <h3 className="font-display font-semibold text-base mb-1">Évolution prévue de l'épargne</h3>
            <p className="text-xs text-muted-foreground mb-4">Projection sur 12 mois selon votre solde mensuel ({fmtEuro(proj.monthly_net)}/mois), provisions annuelles incluses.</p>
            {proj.declining && (
              <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 p-3 mb-4" data-testid="savings-declining-warning">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div className="text-sm text-red-800"><span className="font-semibold">Votre épargne diminue dans le futur.</span> Vos dépenses (courantes + provisions annuelles ramenées au mois) dépassent vos rentrées. Solde mensuel : {fmtEuro(proj.monthly_net)}.</div>
              </div>
            )}
            {!(proj.current_total === 0 && proj.series.every((s) => s.balance === 0)) && (
              <SeriesToggle testid="sav-series-toggle" hidden={savHidden} onToggle={toggleIn(setSavHidden)} items={[
                { key: "balance", label: "Total", color: SAV_COLORS.balance },
                { key: "cash", label: "Cash", color: SAV_COLORS.cash },
                { key: "bourse", label: "Bourse", color: SAV_COLORS.bourse },
              ]} />
            )}
            {proj.current_total === 0 && proj.series.every((s) => s.balance === 0) ? (
              <div className="h-72 flex flex-col items-center justify-center text-center text-sm text-muted-foreground" data-testid="savings-chart-empty">
                <LineChart className="w-8 h-8 mb-3 opacity-40" />
                <p>Aucune donnée à projeter pour l'instant.</p>
                <p className="text-xs mt-1">Ajoutez vos comptes d'épargne et vos rentrées/dépenses exceptionnelles pour voir la courbe.</p>
              </div>
            ) : (
            <div className="h-72" data-testid="savings-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={[{ label: "Aujourd'hui", balance: proj.current_total, cash: proj.cash_total, bourse: proj.invest_total }, ...proj.series]}>
                  <defs>
                    <linearGradient id="sav" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22293A" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#22293A" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={64} />
                  <Tooltip formatter={(v, n) => [fmtEuro(v), n]} contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", fontSize: 13 }} />
                  <ReferenceLine y={0} stroke="hsl(var(--destructive))" strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="balance" name="Total" stroke={SAV_COLORS.balance} strokeWidth={2.5} fill="url(#sav)" hide={savHidden.includes("balance")} />
                  <Area type="monotone" dataKey="cash" name="Cash" stroke={SAV_COLORS.cash} strokeWidth={2} fill={SAV_COLORS.cash} fillOpacity={0} hide={savHidden.includes("cash")} />
                  <Area type="monotone" dataKey="bourse" name="Bourse" stroke={SAV_COLORS.bourse} strokeWidth={2} fill={SAV_COLORS.bourse} fillOpacity={0} hide={savHidden.includes("bourse")} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            )}
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <Card className="p-5 border-border lg:col-span-2">
              <h3 className="font-display font-semibold text-base mb-3">Mes comptes</h3>
              {accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Aucun compte. Ajoutez vos comptes d'épargne (cash, bourse…).</p>
              ) : (
                <div className="space-y-2">
                  {accounts.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 py-2.5 border-b border-border/60 last:border-0" data-testid={`account-row-${a.id}`}>
                      <span className="w-9 h-9 rounded-lg flex items-center justify-center text-white shrink-0" style={{ backgroundColor: a.color }}>
                        {a.type === "bourse" ? <LineChart className="w-4 h-4" /> : <Wallet className="w-4 h-4" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate">{a.name}</div>
                        <div className="text-xs text-muted-foreground">{TYPES[a.type] || a.type}{a.institution ? ` · ${a.institution}` : ""}</div>
                      </div>
                      <div className="font-num text-sm font-medium">{fmtEuro(a.balance)}</div>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeAcc(a.id)} data-testid={`delete-account-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-5 border-border">
              <h3 className="font-display font-semibold text-base mb-1">Solde mensuel type</h3>
              <p className="text-xs text-muted-foreground mb-3">Rentrées moins dépenses courantes (mois type) + provisions annuelles (Vue Annuelle) ramenées au mois.</p>
              <div className="rounded-lg bg-secondary/50 p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-muted-foreground">Dépenses courantes</span><span className="font-num">{fmtEuro(proj.monthly_recurring_expenses)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Rentrées mensuelles</span><span className="font-num text-emerald-600">{fmtEuro(proj.monthly_income)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Dépenses except. / mois</span><span className="font-num" data-testid="proj-exc-expense">{fmtEuro(proj.exceptional_expense_monthly)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Rentrées except. / mois</span><span className="font-num text-emerald-600" data-testid="proj-exc-income">{fmtEuro(proj.exceptional_income_monthly)}</span></div>
                <div className="flex justify-between font-semibold pt-1 mt-1 border-t border-border"><span>Solde mensuel</span><span className={`font-num ${proj.monthly_net < 0 ? "text-destructive" : "text-emerald-600"}`} data-testid="proj-monthly-net">{fmtEuro(proj.monthly_net)}</span></div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Modifiez le mois type dans « Dépenses mensuelles », les provisions dans « Vue Annuelle & Prévisions ».</p>
            </Card>
          </div>

          <div className="pt-2 border-t border-border" />
          <RealEstate onChanged={refreshTotals} />
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="account-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifier le compte" : "Nouveau compte d'épargne"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nom du compte</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Livret, PEA, Portefeuille bourse" data-testid="account-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="account-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="cash">Cash / Compte</SelectItem><SelectItem value="bourse">Investi (Bourse)</SelectItem><SelectItem value="autre">Autre</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Solde actuel (€)</Label><Input type="number" value={form.balance} onChange={(e) => setForm({ ...form, balance: e.target.value })} placeholder="0,00" data-testid="account-balance-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Institution (optionnel)</Label><Input value={form.institution} onChange={(e) => setForm({ ...form, institution: e.target.value })} placeholder="Ex: Belfius, Bolero" data-testid="account-institution-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button onClick={saveAcc} disabled={saving} data-testid="save-account-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
